import axios from "axios";
import HostService from "../../../services/host.service";
import AuthService from "../../../services/auth.service";

/**
 * Relanza un error de Axios como Error con el mensaje legible para el operario
 * @param {Object} error - Error capturado de Axios
 * @param {string} fallback - Mensaje genérico propio del método
 * @throws {Error} Error con .message y .status (undefined si no hubo respuesta)
 */
const relanzar = (error, fallback) => {
    const data = error.response?.data;
    // DECISION: en un 400 del validator el message es genérico ("Parametros incorrectos")
    // y el motivo real viene en details[0].msg; en el resto details es un objeto
    const detalleValidacion = Array.isArray(data?.details) ? data.details[0]?.msg : undefined;
    const wrapped = new Error(detalleValidacion || data?.message || fallback);
    wrapped.status = error.response?.status;
    throw wrapped;
};

/**
 * Servicio para operaciones de compras del feature "ordenes"
 * Contrato: ../api/docs/api/compras.md (respuestas envueltas en { data, message })
 */
class ComprasService {
    /**
     * Obtiene las líneas de un pedido de compra con la información del artículo
     * @param {string} pedido - Número del pedido de compra
     * @param {number} ejercicio - Año del ejercicio
     * @returns {Promise<Array<{numero: string, linea: number, articulo: string, unidades: number,
     *   duplicado: boolean, infoArticulo: (Object|null)}>>} Líneas del pedido ([] si no tiene)
     */
    static async buscar(pedido, ejercicio) {
        try {
            const response = await axios.get(
                HostService.getHost() + 'compras/buscar',
                { ...AuthService.setToken(), params: { pedido, ejercicio } }
            );
            return response.data.data;
        } catch (error) {
            relanzar(error, 'Error al obtener el pedido de compra');
        }
    }

    /**
     * Obtiene los registros de punteo de un producto del pedido
     * @param {string} pedido - Número del pedido de compra
     * @param {string} referencia - Referencia del artículo (con "-línea" si está duplicado)
     * @param {number} ejercicio - Año del ejercicio
     * @returns {Promise<Array<{pedido: string, referencia: string, ejercicio: number, unidades: number,
     *   registrado: string, manual: boolean, usuario: string}>>} Registros ([] si no hay)
     */
    static async getRegistros(pedido, referencia, ejercicio) {
        try {
            const response = await axios.get(
                HostService.getHost() + 'compras/obtener',
                { ...AuthService.setToken(), params: { pedido, referencia, ejercicio } }
            );
            return response.data.data;
        } catch (error) {
            relanzar(error, 'Error al obtener los registros del producto');
        }
    }

    /**
     * Registra las unidades punteadas de un producto del pedido.
     * La firma posicional es la que espera ListOrder como registerAction.
     * @param {string} pedido - Número del pedido de compra
     * @param {string} referencia - Referencia del artículo (con "-línea" si está duplicado)
     * @param {number} unidades - Unidades totales registradas
     * @param {Date|string} registrado - Fecha del registro
     * @param {boolean} manual - Si el registro se hizo a mano
     * @param {number} ejercicio - Año del ejercicio
     * @returns {Promise<Object>} Registro resultante
     */
    static async registrar(pedido, referencia, unidades, registrado, manual, ejercicio) {
        try {
            const response = await axios.post(
                HostService.getHost() + 'compras/registrar',
                { unidades, registrado, manual },
                { ...AuthService.setToken(), params: { pedido, referencia, ejercicio } }
            );
            return response.data.data;
        } catch (error) {
            relanzar(error, 'Error al registrar las unidades');
        }
    }

    /**
     * Obtiene la asignación de personal y la recepción del pedido
     * @param {string} pedido - Número del pedido de compra
     * @param {number} ejercicio - Año del ejercicio
     * @returns {Promise<Array<{pedido: string, preparado: (string|null), revisado: (string|null),
     *   estado: (string|null), recepcionado: (string|null), fechaRecepcion: (string|null)}>>}
     *   Como máximo un elemento ([] si el pedido no tiene asignación)
     */
    static async getPersonas(pedido, ejercicio) {
        try {
            const response = await axios.get(
                HostService.getHost() + 'compras/personas',
                { ...AuthService.setToken(), params: { pedido, ejercicio } }
            );
            return response.data.data;
        } catch (error) {
            relanzar(error, 'Error al obtener el personal del pedido');
        }
    }

    /**
     * Obtiene el estado de los pedidos de compra pendientes del ejercicio
     * @param {number} ejercicio - Año del ejercicio
     * @returns {Promise<Array<Object>>} Pedidos con estado calculado por unidades punteadas
     */
    static async getEstados(ejercicio) {
        try {
            const response = await axios.get(
                HostService.getHost() + `compras/${ejercicio}/estados`,
                AuthService.setToken()
            );
            return response.data.data;
        } catch (error) {
            relanzar(error, 'Error al obtener los estados de los pedidos');
        }
    }

    /**
     * Marca un pedido de compra como recepcionado y asigna el empleado que lo recibe.
     * El recepcionista es un campo propio, independiente de preparado/revisado.
     * @param {string} pedido - Número del pedido de compra
     * @param {number} ejercicio - Año del ejercicio
     * @param {string} recepcionado - Username del empleado que recepciona
     * @returns {Promise<{pedido: string, ejercicio: number, proveedor: (string|null),
     *   estado: string, recepcionado: string, fechaRecepcion: string, usuario: string}>}
     *   Registro de recepción resultante
     */
    static async recepcionar(pedido, ejercicio, recepcionado) {
        try {
            const response = await axios.post(
                HostService.getHost() + 'compras/recepcionar',
                { recepcionado },
                { ...AuthService.setToken(), params: { pedido, ejercicio } }
            );
            return response.data.data;
        } catch (error) {
            relanzar(error, 'Error al recepcionar el pedido');
        }
    }
}

export default ComprasService;
